// Convert from SVG format to whiteboard objects. For simplicity, we convert
// objects that we don't otherwise handle to custom paths. This gives visual
// fidelity, but doesn't allow for editing of many objects.
//
// The code works best with Figma that exports simple SVGs.

import 'path-data-polyfill';
import { type DocData } from '../collaboration/Serialization';
import type Shape from '../objects/Shape';
import type Group from '../fabric/SimpleGroup';
import type WhiteboardController from '../controllers/WhiteboardController';
import { randomId } from '@aha-app/mvc';
import { sanitizeSVG } from './svgSanitizer';
import { parseData } from './jsonValidator';
import Color from 'color';

interface Attributes {
  fill?: string | null;
  fillOpacity?: number;
  fillRule?: string;
  opacity?: number;
  stroke?: string | null;
  strokeWidth?: number;
  strokeOpacity?: number;
  angle?: number;
  scaleX?: number;
  scaleY?: number;
  translateX?: number;
  translateY?: number;
  fontFamily?: string;
  useOffsetX?: number;
  useOffsetY?: number;
}

interface TransformAttributes {
  angle?: number;
  scaleX?: number;
  scaleY?: number;
  translateX?: number;
  translateY?: number;
}

type GroupWithObjects = Group & { objects: string[] };
class SVGImporter {
  zIndex: number;
  objects: Record<string, Partial<Shape | GroupWithObjects>>;

  // The SVG element being imported.
  svgElement: HTMLElement;

  // Promises for async object creation, so we can wait for them all to finish
  // in parallel.
  asyncFuncs: Promise<void>[];

  // List of SVG features that were not supported on import. Will be displayed
  // to the user.
  unsupportedFeatures: Set<string>;

  constructor(private readonly controller: WhiteboardController) {}

  translateColor(
    color: string | null | undefined,
    defaultColor: string = 'transparent'
  ): string {
    if (color === 'none') return 'transparent';
    if (!color) return defaultColor;

    if (color?.startsWith('url')) {
      this.unsupportedFeatures.add('gradients/patterns/image fills');
      return '#DDDDDD';
    }

    // Convert named colors to hex
    const c = Color(color);
    return c.hex();
  }

  convertText(attributes: Attributes, textContent: string): string {
    let s = textContent || '';
    let style = '';
    const data = {};

    if (attributes.fill) {
      style += `color: ${this.translateColor(attributes.fill)};`;
    }
    if (attributes.fontFamily) {
      style += `font-family: &quot;${attributes.fontFamily}&quot;;`;
      data['data-font-family-mark'] = attributes.fontFamily;
    }

    if (style.length > 0 || Object.keys(data).length > 0) {
      const styleString = `style="${style}"`;
      const dataString = Object.keys(data)
        .map(key => `${key}="${data[key]}"`)
        .join(' ');
      s = `<span ${styleString} ${dataString}>${s}</span>`;
    }

    return sanitizeSVG(s);
  }

  pathDataToSVGPath(pathData: any[]): string {
    return pathData
      .map(segment => {
        const values = segment.values
          .map((v: number) => v.toFixed(6))
          .join(',');
        return `${segment.type}${values}`;
      })
      .join('');
  }

  convertElementToPath(element: SVGElement): string {
    if (element instanceof SVGPathElement) {
      return element.getAttribute('d') || '';
    }

    if (!(element as any).getPathData) {
      return '';
    }

    // Use getPathData for other shapes
    const pathData = (element as any).getPathData({ normalize: true });
    return this.pathDataToSVGPath(pathData);
  }

  transformPathToCenter(
    pathData: string,
    offsetX: number,
    offsetY: number
  ): string {
    const numbers = pathData.match(/[-+]?\d*\.?\d+/g)?.map(Number) || [];
    const transformed: number[] = [];

    // First pass: transform all numbers based on command type
    let numberIndex = 0;
    const commands = pathData.match(/[A-Za-z]/g) || [];

    for (let i = 0; i < commands.length; i++) {
      const command = commands[i];

      // Count how many numbers this command needs
      let numCount = 0;
      switch (command.toUpperCase()) {
        case 'M':
        case 'L':
          numCount = 2;
          break;
        case 'H':
          numCount = 1;
          break;
        case 'V':
          numCount = 1;
          break;
        case 'C':
          numCount = 6;
          break;
        case 'S':
          numCount = 4;
          break;
        case 'Q':
          numCount = 4;
          break;
        case 'T':
          numCount = 2;
          break;
        case 'A':
          numCount = 7;
          break;
        case 'Z':
          numCount = 0;
          break;
      }

      // Transform the numbers for this command
      for (let j = 0; j < numCount; j++) {
        const value = numbers[numberIndex + j];
        if (command.toUpperCase() === 'H') {
          // For H command, only transform x coordinate
          transformed.push(value + offsetX);
        } else if (command.toUpperCase() === 'V') {
          // For V command, only transform y coordinate
          transformed.push(value + offsetY);
        } else if (j % 2 === 0) {
          // For other commands, transform x coordinates
          transformed.push(value + offsetX);
        } else {
          // For other commands, transform y coordinates
          transformed.push(value + offsetY);
        }
      }
      numberIndex += numCount;
    }

    // Second pass: reconstruct the path string
    let result = '';
    numberIndex = 0;

    for (let i = 0; i < commands.length; i++) {
      const command = commands[i];
      result += command;

      // Count how many numbers this command needs
      let numCount = 0;
      switch (command.toUpperCase()) {
        case 'M':
        case 'L':
          numCount = 2;
          break;
        case 'H':
        case 'V':
          numCount = 1;
          break;
        case 'C':
          numCount = 6;
          break;
        case 'S':
          numCount = 4;
          break;
        case 'Q':
          numCount = 4;
          break;
        case 'T':
          numCount = 2;
          break;
        case 'A':
          numCount = 7;
          break;
        case 'Z':
          numCount = 0;
          break;
      }

      // Add the transformed numbers
      for (let j = 0; j < numCount; j++) {
        if (j > 0) result += ',';
        result += transformed[numberIndex + j].toFixed(6);
      }
      numberIndex += numCount;
    }

    return result;
  }

  // Parse an SVG `transform` attribute string.
  parseTransform(transformString: string | null): TransformAttributes {
    if (!transformString || typeof transformString !== 'string') {
      return {};
    }

    const transforms: TransformAttributes = {};

    // Regular expression to match transformation functions
    const transformRegex =
      /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
    let match: RegExpExecArray | null;

    while ((match = transformRegex.exec(transformString)) !== null) {
      const [, transformType, paramsString] = match;

      // Parse the parameters - remove extra spaces and split by commas
      const params = paramsString
        .trim()
        .split(/\s*,\s*|\s+/)
        .map(parseFloat)
        .filter(n => !isNaN(n));

      switch (transformType) {
        case 'translate':
          transforms.translateX = params[0] || 0;
          transforms.translateY = params[1] || 0;
          break;

        case 'scale':
          transforms.scaleX = params[0] || 1;
          transforms.scaleY = params.length > 1 ? params[1] : params[0] || 1; // If only one param, use it for both x and y
          break;

        case 'rotate':
          transforms.angle = params[0];
          break;

        case 'matrix':
          if (params.length === 6) {
            const [a, b, c, d, e, f] = params;

            // Extract the translation
            transforms.translateX = e;
            transforms.translateY = f;

            // Extract the scale
            // For scale, we use the length of the transformed unit vectors
            const scaleX = Math.sqrt(a * a + b * b);
            const scaleY = Math.sqrt(c * c + d * d);

            transforms.scaleX = scaleX;
            transforms.scaleY = scaleY;

            // Extract the rotation
            // The rotation angle can be calculated from the transformed x-axis
            let rotationAngle = Math.atan2(b, a) * (180 / Math.PI);

            const det = a * d - b * c;
            if (det < 0) {
              // Check for negative scale which would flip the rotation. However
              // this is not support in whiteboards, so just ignore.
              this.unsupportedFeatures.add('axis flip');
              /*
              if (a > d) {
                transforms.translateY = -transforms.translateY;
              } else {
                transforms.translateX = -transforms.translateX;
              }
              */
            }

            // Ensure rotation is between 0 and 360 degrees
            if (rotationAngle < 0) {
              rotationAngle += 360;
            }

            transforms.angle = rotationAngle;
          }
          break;
      }
    }

    return transforms;
  }

  mergeAttributes(obj1, obj2) {
    // Create a new object to store the merged result
    const result = {};

    // Process the first object
    for (const key in obj1) {
      if (obj1[key] !== undefined) {
        result[key] = obj1[key];
      }
    }

    // Process the second object
    for (const key in obj2) {
      if (obj2[key] !== undefined) {
        result[key] = obj2[key];
      }
    }

    return result;
  }

  prepareAttributes(element: SVGElement): Attributes {
    const attributes: Attributes = {};

    // Only override if the attribute exists on the element
    const stroke = element.getAttribute('stroke');
    if (stroke !== null) attributes.stroke = stroke;

    const strokeWidth = element.getAttribute('stroke-width');
    if (strokeWidth !== null) attributes.strokeWidth = Number(strokeWidth);

    const strokeOpacity = element.getAttribute('stroke-opacity');
    if (strokeOpacity !== null)
      attributes.strokeOpacity = Number(strokeOpacity);

    const fill = element.getAttribute('fill');
    if (fill !== null) attributes.fill = fill;

    const fillOpacity = element.getAttribute('fill-opacity');
    if (fillOpacity !== null) attributes.fillOpacity = Number(fillOpacity);

    const fillRule = element.getAttribute('fill-rule');
    if (fillRule !== null) attributes.fillRule = fillRule;

    const opacity = element.getAttribute('opacity');
    if (opacity !== null) attributes.opacity = Number(opacity);

    const fontFamily = element.getAttribute('font-family');
    if (fontFamily !== null) attributes.fontFamily = fontFamily;

    return attributes;
  }

  strokeProperties(attributes: Attributes): any {
    const stroke = attributes.stroke;

    if (stroke === 'none' || !stroke) {
      return {
        stroke: 'transparent',
        strokeWidth: 1,
        borderOpacity: 1,
      };
    } else {
      return {
        stroke: this.translateColor(stroke),
        strokeWidth: attributes.strokeWidth || 1,
        borderOpacity: attributes.strokeOpacity || 1,
      };
    }
  }

  fillProperties(attributes: Attributes): any {
    const fill = attributes.fill;

    if (fill === 'none' || !fill) {
      return {
        fill: 'transparent',
        fillOpacity: 1,
      };
    } else {
      return {
        fill: this.translateColor(fill),
        fillOpacity: attributes.fillOpacity || 1,
        fillRule: attributes.fillRule,
      };
    }
  }

  processSvgElement(
    element: SVGElement,
    inheritedAttributes: Attributes = {}
  ): string | undefined {
    // Skip empty elements
    if (!element) return undefined;
    if (element.tagName === 'mask') {
      this.unsupportedFeatures.add('mask');
      return undefined;
    }
    if (element.tagName === 'clipPath') {
      this.unsupportedFeatures.add('clipping paths');
      return undefined;
    }
    if (element.tagName === 'defs') {
      return undefined;
    }

    const id: RecordId = randomId();
    const transforms = this.parseTransform(element.getAttribute('transform'));
    const attributes: Attributes = this.mergeAttributes(
      inheritedAttributes,
      this.mergeAttributes(transforms, this.prepareAttributes(element))
    );

    // Process text elements
    if (element.tagName === 'text') {
      const text = element as SVGTextElement;
      const bbox = text.getBBox();
      const fontSize = Number(text.getAttribute('font-size')) || 14;

      this.objects[id] = {
        type: 'shape',
        id,
        z: this.zIndex++,
        shapeName: 'text',
        textContent: this.convertText(attributes, text.textContent || ''),
        left:
          bbox.x - (attributes.translateX || 0) + (attributes.useOffsetX || 0),
        top:
          bbox.y - (attributes.translateY || 0) + (attributes.useOffsetY || 0),
        width: bbox.width,
        height: bbox.height,
        strokeType: null,
        strokeDouble: false,
        scaleX: 1,
        scaleY: 1,
        stroke: null,
        strokeWidth: 0,
        opacity: 1,
        borderOpacity: attributes.strokeOpacity,
        fill: null,
        textAlign: 'left',
        textMargin: 0,
        textOpacity: attributes.fillOpacity || attributes.opacity || 1,
        fontSize: Number(text.getAttribute('font-size')) || 14,
        angle: attributes.angle || 0,
        authorId: '58',
        showAuthor: true,
        maskType: 'none',
        wrapText: bbox.height > 2 * fontSize,
        locked: false,
      };

      return id;
    } else if (element.tagName === 'g') {
      // Create a group object
      const childObjectIds: string[] = [];

      // Process all child elements and collect their IDs
      const children = element.children;
      for (let i = 0; i < children.length; i++) {
        const childElement = children[i] as SVGElement;
        // Process the child element and get its ID, passing inherited attributes
        const childId = this.processSvgElement(childElement, attributes);
        if (childId) {
          childObjectIds.push(childId);
        }
      }

      if (childObjectIds.length > 1) {
        // Create the group object
        this.objects[id] = {
          type: 'group',
          id,
          locked: false,
          objects: childObjectIds,
          z: this.zIndex++,
        };
        return id;
      } else {
        return childObjectIds[0];
      }
    } else if (element.tagName === 'image') {
      /* Notes: 
      
      TL;DR: this code works but in practice we can't import <image> elements.

      When Figma exports images in SVGs it seems to put them inside a <defs> element.
      We skip <defs> so even though we are able to import images correctly they don't get imported
      in practice for Figma. It might be possible to make this work, though, by loading <image> tags
      that are inside <defs> and taking relevant attributes from the <use> tag that refers to the image.
      Here's an example of what Figma exports:

      <svg width="404" height="238" viewBox="0 0 404 238" fill="none"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink">
        <rect width="404" height="238" rx="8" fill="#F7F7F7"/>
        <rect width="404" height="238" rx="8" fill="url(#pattern0_1_3787)"/>
        <defs>
          <pattern id="pattern0_1_3787" patternContentUnits="objectBoundingBox" width="1" height="1">
            <use xlink:href="#image0_1_3787" transform="matrix(0.000577034 0 0 0.000979503 0 -0.0656631)"/>
          </pattern>
          <image id="image0_1_3787" width="1733" height="1155" preserveAspectRatio="none" xlink:href="data:image/jpeg;base64, [... date elided]"/>
        </defs>
      </svg>

      Further, when importing an <image> tag we currently run into Content Security Policy violations in reallybigaha,
      even for data URIs, so we will need to figure out the right change to get them working there. Works fine
      in the whiteboard dev app, though. Because we only care about Figma right now I'm leaving the CSP issue as-is.
       */
      const image = element as SVGImageElement;
      const bbox = image.getBBox();
      const imageZ = this.zIndex++;

      this.asyncFuncs.push(
        (async () => {
          const response = await fetch(image.href.baseVal);
          const blob = await response.blob();

          // Create a File from the blob
          const file = new File([blob], 'image', { type: blob.type });
          const attachmentId: RecordId = await this.controller.uploadAttachment(
            {
              file,
            }
          );

          this.objects[id] = {
            type: 'shape',
            id,
            z: imageZ,
            shapeName: 'image',
            textContent: '',
            left:
              bbox.x +
              (attributes.translateX || 0) +
              (attributes.useOffsetX || 0),
            top:
              bbox.y +
              (attributes.translateY || 0) +
              (attributes.useOffsetY || 0),
            width: Math.max(bbox.width, 1),
            height: Math.max(bbox.height, 1),
            strokeType: null,
            strokeDouble: false,
            scaleX: 1,
            scaleY: 1,
            stroke: null,
            strokeWidth: 0,
            opacity: attributes.fillOpacity || attributes.opacity || 1,
            borderOpacity: attributes.strokeOpacity || 1,
            fill: null,
            angle: attributes.angle || 0,
            showAuthor: true,
            attachmentId,
            contentType: blob.type,
            maskType: 'none',
            locked: false,
          };
        })()
      );

      return id;
    } else if (element.tagName === 'use') {
      // See if we can find the referenced element in the SVG
      const useElement = element as SVGUseElement;
      const href = useElement.href.baseVal;
      const referencedElement = this.svgElement.querySelector(href);
      if (referencedElement) {
        // Add the x, y offsets.
        const x = Number(useElement.getAttribute('x')) || 0;
        const y = Number(useElement.getAttribute('y')) || 0;
        attributes.useOffsetX = x;
        attributes.useOffsetY = y;
        return this.processSvgElement(
          referencedElement as SVGElement,
          attributes
        );
      } else {
        console.log('Referenced element not found', href);
      }
    } else if (
      [
        'rect',
        'ellipse',
        'path',
        'line',
        'polygon',
        'circle',
        'polylines',
      ].includes(element.tagName)
    ) {
      if (!(element as any).getBBox) {
        console.log(
          `element is not SVGGraphicElement ${element.tagName}`,
          element
        );
        return undefined;
      }
      // Process other SVG elements
      const bbox = (element as SVGGraphicsElement).getBBox();
      const pathData = this.convertElementToPath(element);

      // Skip if path is empty or only contains whitespace
      if (!pathData || pathData.trim() === '') {
        return undefined;
      }

      const transformedPath = this.transformPathToCenter(
        pathData,
        -(bbox.x + bbox.width / 2),
        -(bbox.y + bbox.height / 2)
      );

      this.objects[id] = {
        type: 'shape',
        id,
        z: this.zIndex++,
        shapeName: 'customPath',
        textContent: '',
        left:
          bbox.x + (attributes.translateX || 0) + (attributes.useOffsetX || 0),
        top:
          bbox.y + (attributes.translateY || 0) + (attributes.useOffsetY || 0),
        width: Math.max(bbox.width, 1),
        height: Math.max(bbox.height, 1),
        strokeType: null,
        strokeDouble: false,
        scaleX: 1,
        scaleY: 1,
        ...this.strokeProperties(attributes),
        ...this.fillProperties(attributes),
        opacity: attributes.fillOpacity || attributes.opacity || 1,
        angle: attributes.angle || 0,
        showAuthor: true,
        maskType: 'none',
        shapePath: transformedPath,
        locked: false,
      };

      return id;
    } else {
      console.log('Unsupported element', element.tagName);
    }
  }

  async import(unsafeSVGString: string): Promise<DocData<Partial<Shape>>> {
    const sanitizedString = sanitizeSVG(unsafeSVGString);
    unsafeSVGString = '';

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(sanitizedString, 'image/svg+xml');
    this.svgElement = svgDoc.documentElement;

    // Add SVG to DOM but hide it
    this.svgElement.style.position = 'absolute';
    this.svgElement.style.visibility = 'hidden';
    this.svgElement.style.pointerEvents = 'none';
    document.body.appendChild(this.svgElement);

    this.zIndex = 10;
    this.objects = {};
    this.asyncFuncs = [];
    this.unsupportedFeatures = new Set();

    // Process all SVG elements recursively
    const elements = this.svgElement.children;
    for (let i = 0; i < elements.length; i++) {
      this.processSvgElement(elements[i] as SVGElement);
    }

    // Wait for all async promises to resolve.
    await Promise.allSettled(this.asyncFuncs);

    // Remove SVG from DOM
    document.body.removeChild(this.svgElement);

    if (this.unsupportedFeatures.size > 0) {
      this.controller.actionShowMessage(
        `Some features of the SVG were not supported: ${Array.from(
          this.unsupportedFeatures
        ).join(', ')}.`
      );
    }

    // Sanitize the output.
    const sanitizedObjects = {};
    for (const id in this.objects) {
      const obj = this.objects[id];
      const result = parseData(obj);
      if (result.success) {
        const { data } = result;
        /* The JSON validator strips `attachmentId` and `contentType` out and we need to 
        restore them for the image to work. I didn't change the validator because we also
        use it for things like AI and we don't want AI to be setting values for these fields */
        if (data.shapeName === 'image') {
          data.attachmentId = obj.attachmentId;
          data.contentType = obj.contentType;
        }
        // TODO: consider calling `postParse`.
        sanitizedObjects[id] = data;
      } else {
        console.error(`Failed to parse object ${id}`, result.error);
      }
    }

    return { objects: sanitizedObjects };
  }
}

export default SVGImporter;
